import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { supportEmailAddress } from 'constants/other';

// Default props for the component, especially for the resetErrorBoundary function
const defaultProps = {
	error: { message: 'An unknown error occurred.' }, // Provide a default error object
	resetErrorBoundary: () => {
		console.warn('resetErrorBoundary function was not provided to ErrorFallback.');
		// Fallback behavior: try to reload the page if no reset function is given
		window.location.reload();
	},
};

/**
 * ErrorFallback Component
 * A Material-UI styled fallback component for React Error Boundaries.
 *
 * @param {object} props - The component's props.
 * @param {Error} props.error - The error object caught by the Error Boundary.
 * @param {Function} props.resetErrorBoundary - A function to reset the Error Boundary's state.
 */
function ErrorFallback({ error, resetErrorBoundary } = defaultProps) {
	// Ensure resetErrorBoundary is a function, even if not passed (using defaultProps)
	const handleReset = () => {
		if (typeof resetErrorBoundary === 'function') {
			resetErrorBoundary();
		} else {
			// Fallback if defaultProps didn't catch it or it was explicitly passed as non-function
			console.warn('resetErrorBoundary is not a function, attempting page reload.');
			window.location.reload();
		}
	};

	const subheaders = [
		'Oops! 😕 A glitch occurred in this section.',
		'Something glitched here! 🔧 Oops!',
		'Uh-oh! 🚨 This part hit a snag.',
		'Oops! 🛠 This feature ran into a glitch.',
		"Glitch detected! ⚡ Oops, this section isn't working right.",
		'Oops! 🤖 A mysterious glitch just happened.',
		'Oh no! 😬 This part of the app encountered an unexpected error.',
		'Oops, this module tripped over a glitch! 🌀',
		'Unexpected glitch detected! 🚀 Oops, we’re on it',
		'Oops! This section encountered an unexpected issue 🚨',
		'This feature encountered an unexpected error.',
	];

	const getRandomElement = (arr) => arr[Math.floor(Math.random() * arr.length)];

	return (
		<Container
			component="main"
			maxWidth="sm"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '100vh', // Full viewport height
				textAlign: 'center',
			}}
		>
			<Paper variant="outlined" sx={{ padding: 4 }}>
				<Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
					{getRandomElement(subheaders)}
				</Typography>

				<Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
					We've been automatically notified and are working to fix the issue. Please try again, or you can{' '}
					<a href={`mailto:${supportEmailAddress}`} title={supportEmailAddress}>
						contact support
					</a>
					.
				</Typography>

				{/* You can optionally display error details during development */}
				{process.env.NODE_ENV === 'development' && error && (
					<Box
						sx={{
							my: 2,
							p: 2,
							border: '1px dashed grey',
							borderRadius: 1,
							backgroundColor: 'rgba(0,0,0,0.05)',
							maxWidth: '100%',
							overflowX: 'auto',
							textAlign: 'left',
						}}
					>
						<Typography variant="caption" display="block" gutterBottom>
							<strong>Error Details (Development Mode):</strong>
						</Typography>
						<Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
							{error.message || 'No error message available.'}
							{error.stack && `\n\nStack Trace:\n${error.stack}`}
						</Typography>
					</Box>
				)}

				<Button
					variant="contained"
					color="primary"
					onClick={handleReset} // Use the safe handleReset
				>
					Try Again
				</Button>
			</Paper>
		</Container>
	);
}

// Assign default props to the component
ErrorFallback.defaultProps = defaultProps;

export default ErrorFallback;
